import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import {
  LuShoppingCart, LuStore, LuStar, LuGift, LuDownload, LuSearch, LuPlus,
  LuReceipt, LuTruck, LuScale, LuUsers, LuUnlock, LuLink2, LuLayoutGrid, LuCode,
} from "react-icons/lu";
import { Popover } from "antd";
import Icon from "../../assets/icons";
import Tabs from "../../components/tab";
import Table from "../../components/table";
import Button from "../../components/button";
import useRequest from "../../components/hooks/use-request";
import DateRange from "../../components/date-range";
import { TailSpin } from "react-loader-spinner";
import { formatDate } from "../../utilities/functions";

// The DateRange picker returns "dd/MM/yyyy - dd/MM/yyyy"; convert to ISO from/to
const parseDateRange = (range: string): { from?: string; to?: string } => {
  if (!range) return {};
  const parts = range.split(" - ");
  if (parts.length !== 2) return {};
  const toISO = (dmy: string) => {
    const [d, m, y] = dmy.trim().split("/");
    return `${y}-${m}-${d}`;
  };
  return { from: toISO(parts[0]), to: toISO(parts[1]) };
};

const EmptyState = ({ label = "No data available" }: { label?: string }) => (
  <div className="flex justify-center items-center h-64">
    <p className="text-gray-500 text-lg">{label}</p>
  </div>
);

const PaginationRow = ({ currentPage, totalPages, setCurrentPage, count }: any) => (
  <div className="mt-4 flex justify-between items-center text-[13px] text-gray-600">
    <span>{count} result{count === 1 ? "" : "s"} &bull; page {currentPage} of {totalPages}</span>
    <div className="flex items-center gap-3">
      <button onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Prev</button>
      <button onClick={() => setCurrentPage((p: number) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Next</button>
    </div>
  </div>
);

// Shared sub-components
const avatarColors = ["#0979A1", "#E85D04", "#7B2D8B", "#2D6A4F", "#D62828", "#023E8A", "#B5179E"];
const avatarColor = (name: string) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const UserAvatar = ({ name }: { name: string }) => (
  <div
    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-semibold shrink-0"
    style={{ backgroundColor: avatarColor(name) }}>
    {name?.charAt(0).toUpperCase()}
  </div>
);

const UserCell = ({ name, email }: { name: string; email?: string }) => (
  <div className="flex items-center gap-2">
    <UserAvatar name={name} />
    <div>
      <p className="text-[13px] font-medium">{name}</p>
      {email && <p className="text-[11px] text-gray-500">{email}</p>}
    </div>
  </div>
);

const RankBadge = ({ rank }: { rank: number }) => {
  if (rank === 1) return <Icon name="goldMedal" />;
  if (rank === 2) return <Icon name="silverMedal" />;
  if (rank === 3) return <Icon name="bronzeMedal" />;
  return <span className="text-[14px] font-semibold text-gray-600">{rank}</span>;
};

const ProgressBar = ({ pct }: { pct: number }) => {
  const color = pct >= 90 ? "#DC2626" : pct >= 70 ? "#F97316" : pct >= 50 ? "#EAB308" : "#22C55E";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
      <span className="text-[12px]">{pct}%</span>
    </div>
  );
};

const milestoneMeta: Record<string, { icon: JSX.Element; color: string }> = {
  transaction: { icon: <LuReceipt size={14} />, color: "#22C55E" },
  logistics: { icon: <LuTruck size={14} />, color: "#3B82F6" },
  mediation_log: { icon: <LuScale size={14} />, color: "#0979A1" },
  referrals: { icon: <LuUsers size={14} />, color: "#F97316" },
  partial_release: { icon: <LuUnlock size={14} />, color: "#EAB308" },
  quick_links: { icon: <LuLink2 size={14} />, color: "#8B5CF6" },
  catalog_setup: { icon: <LuLayoutGrid size={14} />, color: "#EC4899" },
  business_api: { icon: <LuCode size={14} />, color: "#6B7280" },
};

// Milestone breakdown, fetched live per merchant when the popover opens.
// merchantId can be null (auto-provisioned merchant) — then it's plain text.
const MilestonePopover = ({ merchantId, label, from, to }: any) => {
  const userToken = localStorage.getItem("token");
  const [milestones, setMilestones] = useState<any[]>([]);
  const { makeRequest, loading } = useRequest(
    `/campaigns/merchants/${merchantId}/milestones`,
    "GET",
    { Authorization: `Bearer ${userToken}` }
  );

  const handleOpenChange = async (open: boolean) => {
    if (!open || !merchantId) return;
    const params: Record<string, any> = {};
    if (from && to) {
      params.from = from;
      params.to = to;
    }
    const [res] = await makeRequest(
      undefined,
      Object.keys(params).length ? params : undefined
    );
    if (res?.data) setMilestones(res.data.milestones || []);
  };

  if (!merchantId) {
    return <span className="text-[13px] text-gray-600">{label || "—"}</span>;
  }

  return (
    <Popover
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottom"
      content={
        <div className="w-60 py-1">
          {loading ? (
            <div className="flex justify-center py-4">
              <TailSpin color="skyblue" height={22} width={22} />
            </div>
          ) : milestones.length === 0 ? (
            <p className="text-[12px] text-gray-400 py-2 text-center">No milestone data</p>
          ) : (
            milestones.map((m: any) => {
              const meta = milestoneMeta[m.key] || {};
              return (
                <div
                  key={m.id}
                  className={`flex items-center justify-between py-1.5 ${m.dormant ? "opacity-40" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span style={{ color: meta.color }}>{meta.icon}</span>
                    <span className="text-[13px]">{m.name}</span>
                    {!m.isOneTime && !m.dormant && m.timesCompleted > 0 && (
                      <span className="text-[11px] text-gray-400">&times;{m.timesCompleted}</span>
                    )}
                  </div>
                  <span className="text-[12px] text-gray-500">{m.points} points</span>
                </div>
              );
            })
          )}
        </div>
      }>
      <button className="text-[#0979A1] text-[13px] font-medium hover:underline">
        {label}
      </button>
    </Popover>
  );
};

const StatusBadge = ({ label }: { label: string }) => {
  const isActive = label?.toLowerCase() === "active";
  const isDisabled = label?.toLowerCase() === "disabled";
  const isCapReached = label?.toLowerCase() === "cap reached";
  const cls = isActive
    ? "bg-green-50 text-green-700"
    : isDisabled || isCapReached
    ? "bg-red-50 text-red-600"
    : "bg-gray-100 text-gray-600";
  return <span className={`px-2 py-0.5 rounded text-[12px] font-medium ${cls}`}>{label}</span>;
};

// ─── Merchants Tab ───────────────────────────────────────────────────────────
const MerchantsTab = ({ from, to }: any) => {
  const userToken = localStorage.getItem("token");
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { makeRequest, loading } = useRequest("/campaigns/merchants/leaderboard", "GET", { Authorization: `Bearer ${userToken}` });

  useEffect(() => {
    setCurrentPage(1);
  }, [from, to]);

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { page: currentPage };
      if (from && to) {
        params.from = from;
        params.to = to;
      }
      const [res] = await makeRequest(undefined, params);
      if (res?.data) {
        setData(res.data.leaderboard || []);
        setTotalPages(res.data.totalPages || 1);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, from, to]);

  const columns = [
    { header: "Rank", accessor: "rank", render: (row: any) => <RankBadge rank={row.rank} /> },
    { header: "Merchant", accessor: "merchantName", render: (row: any) => <UserCell name={row.merchantName} email={row.merchantEmail} /> },
    { header: "Total Points", accessor: "totalPoints" },
    { header: "Eligible Volume (₦)", accessor: "eligibleVolume", render: (row: any) => (row.eligibleVolume || 0).toLocaleString() },
    { header: "Unique Buyers", accessor: "uniqueBuyers" },
    { header: "Completed Txns", accessor: "completedTxns" },
    {
      header: "Milestones", accessor: "milestonesCompleted",
      render: (row: any) => <MilestonePopover merchantId={row.userId ?? row.merchantId} label={row.milestonesCompleted} from={from} to={to} />,
    },
    { header: "Voucher Used (₦)", accessor: "voucherUsed", render: (row: any) => (row.voucherUsed || 0).toLocaleString() },
    { header: "Voucher Balance (₦)", accessor: "voucherBalance", render: (row: any) => (row.voucherBalance || 0).toLocaleString() },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-[16px] font-semibold">Merchant Leaderboard</h3>
        <button className="flex items-center gap-2 text-[13px] border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50">
          <LuDownload size={14} /> Export
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64"><TailSpin color="skyblue" /></div>
      ) : data.length > 0 ? (
        <Table columns={columns} data={data} onUserClick={() => {}} selectedUserId={null} sn={false} />
      ) : (
        <EmptyState />
      )}
      <PaginationRow currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} count={data.length} />
    </div>
  );
};

// ─── Buyers Tab ──────────────────────────────────────────────────────────────
const BuyersTab = ({ from, to }: any) => {
  const userToken = localStorage.getItem("token");
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { makeRequest, loading } = useRequest("/campaigns/buyers/leaderboard", "GET", { Authorization: `Bearer ${userToken}` });

  useEffect(() => {
    setCurrentPage(1);
  }, [from, to]);

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { page: currentPage };
      if (from && to) {
        params.from = from;
        params.to = to;
      }
      const [res] = await makeRequest(undefined, params);
      if (res?.data) {
        setData(res.data.leaderboard || []);
        setTotalPages(res.data.totalPages || 1);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, from, to]);

  const columns = [
    { header: "Rank", accessor: "rank", render: (row: any) => <RankBadge rank={row.rank} /> },
    { header: "Buyer", accessor: "buyerName", render: (row: any) => <UserCell name={row.buyerName} email={row.buyerEmail} /> },
    { header: "Total Volume (₦)", accessor: "totalVolume", render: (row: any) => (row.totalVolume || 0).toLocaleString() },
    { header: "Completed Txns", accessor: "completedTxns" },
    { header: "Referrals", accessor: "referrals" },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-[16px] font-semibold">Buyer Leaderboard</h3>
        <button className="flex items-center gap-2 text-[13px] border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50">
          <LuDownload size={14} /> Export
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64"><TailSpin color="skyblue" /></div>
      ) : data.length > 0 ? (
        <Table columns={columns} data={data} onUserClick={() => {}} selectedUserId={null} sn={false} />
      ) : (
        <EmptyState />
      )}
      <PaginationRow currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} count={data.length} />
    </div>
  );
};

// ─── Voucher Management Tab ───────────────────────────────────────────────────
// Pool pie slice colours, keyed by name so filtering zero slices keeps colours stable
const POOL_COLORS: Record<string, string> = {
  Redeemed: "#0979A1",
  Distributed: "#F97316",
  Expired: "#EAB308",
};

const VoucherManagementTab = ({ from, to }: any) => {
  const userToken = localStorage.getItem("token");
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${userToken}` };
  const [allocations, setAllocations] = useState<any[]>([]);
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [poolStats, setPoolStats] = useState<any>(null);
  const [voucherSearch, setVoucherSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const { makeRequest: getVouchers, loading } = useRequest("/campaigns/vouchers", "GET", headers);
  const { makeRequest: getPoolStats } = useRequest("/campaigns/vouchers/stats", "GET", headers);
  const { makeRequest: getHistory } = useRequest("/campaigns/vouchers/redemption-history", "GET", headers);

  useEffect(() => {
    setCurrentPage(1);
  }, [from, to]);

  useEffect(() => {
    const fetch = async () => {
      const params: Record<string, any> = { type: "merchant", page: currentPage };
      if (from && to) {
        params.from = from;
        params.to = to;
      }
      const [res] = await getVouchers(undefined, params);
      if (res?.data) {
        setAllocations(res.data.allocations || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, from, to]);

  useEffect(() => {
    const fetch = async () => {
      const dateParams = from && to ? { from, to } : undefined;
      const [statsRes] = await getPoolStats(undefined, dateParams);
      if (statsRes?.data) setPoolStats(statsRes.data);
      const histParams: Record<string, any> = { limit: 5 };
      if (from && to) {
        histParams.from = from;
        histParams.to = to;
      }
      const [histRes] = await getHistory(undefined, histParams);
      if (histRes?.data) setRedemptions(histRes.data.history || []);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Build pie slices from the flat stats object; omit Expired when it's zero
  const poolSlices = poolStats
    ? [
        { name: "Redeemed", amount: poolStats.redeemed || 0, pct: poolStats.redeemedPct || 0 },
        { name: "Distributed", amount: poolStats.distributed || 0, pct: poolStats.distributedPct || 0 },
        ...(poolStats.expired > 0
          ? [{ name: "Expired", amount: poolStats.expired, pct: poolStats.expiredPct || 0 }]
          : []),
      ]
    : [];

  // Only slices with a value are drawn, so a single 100% slice renders a full ring
  const pieData = poolSlices.filter((s) => s.amount > 0);

  const summaryColumns = [
    { header: "Merchant", accessor: "recipientName", render: (row: any) => <UserCell name={row.recipientName} email={row.recipientEmail} /> },
    { header: "Allocated (₦)", accessor: "totalAllocated", render: (row: any) => (row.totalAllocated || 0).toLocaleString() },
    { header: "Redeemed (₦)", accessor: "totalRedeemed", render: (row: any) => (row.totalRedeemed || 0).toLocaleString() },
    { header: "Balance (₦)", accessor: "balance", render: (row: any) => row.balance != null ? (row.balance || 0).toLocaleString() : "-" },
    {
      header: "Redemption Rate", accessor: "redemptionRate",
      render: (row: any) => <ProgressBar pct={row.redemptionRate || 0} />,
    },
    {
      header: "", accessor: "recipientId",
      render: (row: any) => {
        const id = row.recipientId ?? row.userId ?? row.id;
        return id ? (
          <button
            onClick={() => navigate(`/campaign/voucher-history/${id}`, { state: { from, to } })}
            className="text-[#0979A1] text-[13px] font-medium hover:underline whitespace-nowrap">
            View history
          </button>
        ) : null;
      },
    },
  ];

  const redemptionColumns = [
    { header: "Merchant", accessor: "merchantName", render: (row: any) => <UserCell name={row.merchantName} email={row.merchantEmail} /> },
    {
      header: "Buyer", accessor: "buyerName",
      render: (row: any) => <UserCell name={row.buyerName || row.buyerEmail} email={row.buyerName ? row.buyerEmail : undefined} />,
    },
    { header: "Amount (₦)", accessor: "amount", render: (row: any) => (row.amount || 0).toLocaleString() },
    {
      header: "Transaction ID", accessor: "transactionId",
      render: (row: any) => <span className="text-[#0979A1] font-medium">{row.transactionId}</span>,
    },
    { header: "Redeemed On", accessor: "redeemedOn", render: (row: any) => row.redeemedOn ? formatDate(row.redeemedOn) : "-" },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" variant="primary" type="button" onClick={() => navigate("/campaign/allocate-voucher", { state: { from, to } })}>
          <span className="flex items-center gap-1"><LuPlus size={14} /> Allocate Voucher</span>
        </Button>
      </div>

      {/* Merchant Voucher Summary */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[16px] font-semibold">Merchant Voucher Summary</h3>
          <div className="relative">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              value={voucherSearch}
              onChange={(e) => setVoucherSearch(e.target.value)}
              placeholder="Search by merchant name"
              className="border border-gray-200 rounded-lg pl-9 pr-4 py-1.5 text-[13px] w-56 outline-none focus:border-[#0979A1]"
            />
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-64"><TailSpin color="skyblue" /></div>
        ) : allocations.length > 0 ? (
          <Table columns={summaryColumns} data={allocations} onUserClick={() => {}} selectedUserId={null} sn={false} />
        ) : (
          <EmptyState label="No voucher allocations yet" />
        )}
        <div className="mt-3 flex justify-between items-center text-[13px] text-gray-600">
          <span>{allocations.length} of {totalItems} results</span>
          <div className="flex items-center gap-3">
            <span>{currentPage} of {totalPages} pages</span>
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 text-[13px]">Prev</button>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 text-[13px]">Next</button>
          </div>
        </div>
      </div>

      {/* Redemption History + Pie Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[16px] font-semibold">Voucher Redemption History</h3>
            <Link to="/campaign/voucher-redemptions" className="text-[#0979A1] text-[13px] font-medium">See all</Link>
          </div>
          {redemptions.length > 0 ? (
            <Table columns={redemptionColumns} data={redemptions} onUserClick={() => {}} selectedUserId={null} sn={false} />
          ) : (
            <EmptyState label="No redemptions yet" />
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col items-center">
          <h3 className="text-[16px] font-semibold mb-3 self-start">Voucher Pool Breakdown</h3>
          <PieChart width={200} height={200}>
            <Pie data={pieData} cx={100} cy={100} innerRadius={55} outerRadius={90} dataKey="amount" paddingAngle={pieData.length > 1 ? 2 : 0} stroke="none">
              {pieData.map((s: any, i: number) => (
                <Cell key={i} fill={POOL_COLORS[s.name] || "#D9D9D9"} />
              ))}
            </Pie>
            <Tooltip formatter={(v: any) => `₦${Number(v).toLocaleString()}`} />
          </PieChart>
          <div className="text-center -mt-2 mb-3">
            <p className="text-[12px] text-gray-500">Total Pool</p>
            <p className="text-[16px] font-bold">₦{(poolStats?.totalPool || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="w-full space-y-1.5">
            {poolSlices.map((d: any, i: number) => (
              <div key={i} className="flex justify-between items-center text-[12px]">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: POOL_COLORS[d.name] || "#D9D9D9" }} />
                  <span>{d.name}</span>
                </div>
                <span className="font-medium">₦{(d.amount || 0).toLocaleString()}({d.pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Promo Discounts Tab ──────────────────────────────────────────────────────
const PromoDiscountsTab = () => {
  const [data] = useState<any[]>([]);
  const [userType, setUserType] = useState("Merchant");
  const [promoStatus, setPromoStatus] = useState("All");
  const [capStatus, setCapStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);
  const [totalItems] = useState(0);

  // Promo Discounts is not yet wired to the backend. The real endpoints
  // (/campaigns/catalog-promo/*) are pending integration, so no request is made
  // here — this avoids calling the non-existent /campaign/promo-discounts route.

  const handleApply = () => { setCurrentPage(1); };
  const handleReset = () => { setUserType("Merchant"); setPromoStatus("All"); setCapStatus("All"); setSearch(""); setCurrentPage(1); };

  const merchantCols = [
    { header: "Merchant", accessor: "fullname", render: (row: any) => <UserCell name={row.fullname} email={row.email} /> },
    { header: "Merchant Cap (₦)", accessor: "cap", render: (row: any) => (row.cap || 0).toLocaleString() },
    { header: "Discount Disbursed (₦)", accessor: "disbursed", render: (row: any) => (row.disbursed || 0).toLocaleString() },
    { header: "Transactions", accessor: "transactions" },
    { header: "Unique Buyers", accessor: "uniqueBuyers" },
    { header: "Cap Utilization", accessor: "capUtilization", render: (row: any) => <ProgressBar pct={row.capUtilization || 0} /> },
    { header: "Cap Status", accessor: "capStatus", render: (row: any) => <StatusBadge label={row.capStatus} /> },
    { header: "Promo Status", accessor: "promoStatus", render: (row: any) => <StatusBadge label={row.promoStatus} /> },
  ];

  const buyerCols = [
    { header: "Buyer", accessor: "fullname", render: (row: any) => <UserCell name={row.fullname} email={row.email} /> },
    { header: "Lifetime Cap (₦)", accessor: "cap", render: (row: any) => (row.cap || 0).toLocaleString() },
    { header: "Discount Disbursed (₦)", accessor: "disbursed", render: (row: any) => (row.disbursed || 0).toLocaleString() },
    { header: "Transactions", accessor: "transactions" },
    { header: "Cap Utilization", accessor: "capUtilization", render: (row: any) => <ProgressBar pct={row.capUtilization || 0} /> },
    { header: "Cap Status", accessor: "capStatus", render: (row: any) => <StatusBadge label={row.capStatus} /> },
    { header: "Promo Status", accessor: "promoStatus", render: (row: any) => <StatusBadge label={row.promoStatus} /> },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email..." className="border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-[13px] w-60 outline-none focus:border-[#0979A1]" />
        </div>
        <div className="ml-auto flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[11px] text-gray-500 block mb-0.5">User type</label>
            <select value={userType} onChange={(e) => setUserType(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none">
              <option>Merchant</option><option>Buyer</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 block mb-0.5">Promo Status</label>
            <select value={promoStatus} onChange={(e) => setPromoStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none">
              <option>All</option><option>Active</option><option>Disabled</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 block mb-0.5">Cap Status</label>
            <select value={capStatus} onChange={(e) => setCapStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none">
              <option>All</option><option>Active</option><option>Cap reached</option>
            </select>
          </div>
          <button onClick={handleReset} className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50">Reset</button>
          <Button size="sm" variant="primary" type="button" onClick={handleApply}>Apply</Button>
        </div>
      </div>

      <h3 className="text-[16px] font-semibold mb-3">
        {userType === "Merchant" ? "Merchants Promo Summary" : "Buyers Promo Summary"}
      </h3>
      <Table columns={userType === "Merchant" ? merchantCols : buyerCols} data={data} onUserClick={() => {}} selectedUserId={null} sn={false} />

      <div className="mt-4 flex justify-between items-center text-[13px] text-gray-600">
        <span>1 — {Math.min(data.length, 10)} of {totalItems} results</span>
        <div className="flex items-center gap-3">
          <span>{currentPage} of {totalPages} pages</span>
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 text-[13px]">Prev</button>
          <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 text-[13px]">Next</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Campaign Component ──────────────────────────────────────────────────
const StatCard = ({ icon, iconBg, title, value }: any) => (
  <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>{icon}</div>
    <div>
      <p className="text-[12px] text-gray-500">{title}</p>
      <p className="text-[20px] font-bold leading-tight">{value}</p>
    </div>
  </div>
);

const TABS = ["Merchants", "Buyers", "Voucher Management", "Promo Discounts (Catalog)"];

const Campaign = () => {
  const userToken = localStorage.getItem("token");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || "Merchants");
  const [stats, setStats] = useState<any>({});
  // Persist the selected range so it survives a page reload
  const [dateRange, setDateRange] = useState(
    () => localStorage.getItem("campaign_date_range") || ""
  );

  const handleDateRangeChange = (val: string) => {
    setDateRange(val);
    if (val) localStorage.setItem("campaign_date_range", val);
    else localStorage.removeItem("campaign_date_range");
  };

  // The campaign endpoints require from/to — default to Jan 1 2024 → today
  // until the user picks a range, so leaderboards show the full history.
  const monthDefaults = useMemo(() => {
    const now = new Date();
    const start = new Date(2024, 0, 1);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return { from: iso(start), to: iso(now), label: `${label(start)} - ${label(now)}` };
  }, []);

  const picked = parseDateRange(dateRange);
  const from = picked.from || monthDefaults.from;
  const to = picked.to || monthDefaults.to;

  const { makeRequest: getStats } = useRequest("/campaigns/overview", "GET", { Authorization: `Bearer ${userToken}` });

  useEffect(() => {
    const fetch = async () => {
      const [res] = await getStats(undefined, { from, to });
      if (res?.data) setStats(res.data);
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  return (
    <>
      <div className="flex justify-between items-center w-[95%]">
        <h2 className="text-[24px] font-bold">Campaign Overview</h2>
        <div className="flex items-center gap-3">
          <DateRange placeholder={dateRange || monthDefaults.label} onChange={handleDateRangeChange} />
          <button className="flex items-center gap-2 text-[14px] border border-gray-300 rounded-lg px-3 py-2 font-medium hover:bg-gray-50">
            <LuDownload size={15} /> Export
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5 w-[95%]">
        <StatCard icon={<LuShoppingCart size={18} color="#0979A1" />} iconBg="bg-[#E8F7FA]" title="Active Buyers" value={(stats.activeBuyers ?? 0).toLocaleString()} />
        <StatCard icon={<LuStore size={18} color="#7B2D8B" />} iconBg="bg-purple-50" title="Active Merchants" value={(stats.activeMerchants ?? 0).toLocaleString()} />
        <StatCard icon={<LuStar size={18} color="#E85D04" />} iconBg="bg-orange-50" title="Total Points Awarded" value={(stats.totalPointsAwarded ?? 0).toLocaleString()} />
        <StatCard icon={<LuGift size={18} color="#D62828" />} iconBg="bg-red-50" title="Total Vouchers Redeemed" value={(stats.totalVouchersRedeemed ?? 0).toLocaleString()} />
      </div>

      <div className="mt-6 w-[95%]">
        <Tabs activeTab={activeTab} tabs={TABS} setActiveTab={setActiveTab} variant="underline" className="mb-6" />
        {activeTab === "Merchants" && <MerchantsTab from={from} to={to} />}
        {activeTab === "Buyers" && <BuyersTab from={from} to={to} />}
        {activeTab === "Voucher Management" && <VoucherManagementTab from={from} to={to} />}
        {activeTab === "Promo Discounts (Catalog)" && <PromoDiscountsTab />}
      </div>
    </>
  );
};

export default Campaign;
